/**
 * Centralização de cores, sprites e configurações visuais.
 * Mapa v2: área central cercada, defesa, torres.
 */

// ─── Sprite Paths (servidos de public/) ───

export const SPRITES = {
  // Personagens (spritesheets 64x112, frames 16x16, 4 cols × 7 rows)
  player: 'sprites/characters/hunter.png',
  workerCacador: 'sprites/characters/caveman.png',
  workerAjudante: 'sprites/characters/villager2.png',
  workerGarcom: 'sprites/characters/villager3.png',
  workerContador: 'sprites/characters/oldman.png',
  workerArqueiro: 'sprites/characters/ninja.png',
  client1: 'sprites/characters/villager.png',
  client2: 'sprites/characters/villager4.png',
  client3: 'sprites/characters/villager5.png',

  // Monstros (spritesheets 64x64, frames 16x16, 4 cols × 4 rows)
  selvagem: 'sprites/monsters/bear.png',
  agressivo: 'sprites/monsters/spider.png',
  destruidor: 'sprites/monsters/trex.png',
  voador: 'sprites/monsters/dragon.png',

  // Itens (imagens únicas)
  meat: 'sprites/items/meat.png',
  yakitori: 'sprites/items/yakitori.png',
  goldcoin: 'sprites/items/goldcoin.png',

  // FX
  fire: 'sprites/fx/fire.png',  // 96x12, 8 frames de 12x12
} as const;

/** Todos os paths para preload */
export const ALL_SPRITE_PATHS = Object.values(SPRITES);

/** Config dos spritesheets de personagens (16x16 frames) */
export const CHAR_SHEET = {
  frameW: 16,
  frameH: 16,
  cols: 4,
  rowDown: 0,
  rowUp: 1,
  rowLeft: 2,
  rowRight: 3,
} as const;

/** Config dos spritesheets de monstros (16x16 frames, 4x4) */
export const MONSTER_SHEET = {
  frameW: 16,
  frameH: 16,
  cols: 4,
  rowDown: 0,
  rowUp: 1,
  rowLeft: 2,
  rowRight: 3,
} as const;

/** Escala dos sprites */
export const SPRITE_SCALE = {
  character: 2.0,
  monster: 2.0,
  item: 2.0,
  coin: 2.5,
  fire: 2.0,
} as const;

// ─── Cores (para UI, estações, terreno, cerca) ───

export const COLORS = {
  player: 0x2d8a4e,
  animal: 0x8b4513,
  client: 0xdaa520,
  // Itens
  carneRaw: 0xcc3333,
  carneCooked: 0xff8c00,
  coin: 0xffd700,
  wood: 0x8b6914,
  // Estações
  mesaAssador: 0x6b4226,
  fogueira: 0xff4500,
  balcao: 0x4a7c59,
  banco: 0x4a5568,
  // Workers
  workerCacador: 0xcc4444,
  workerAjudante: 0x44aa88,
  workerGarcom: 0x4488cc,
  workerContador: 0x9966cc,
  workerArqueiro: 0x88cc44,
  // Defesas
  cerca: 0x8b6914,
  cercaDamaged: 0xcc3333,
  torre: 0x555555,
  // Terreno
  grass: 0x3a7d44,
  grassLight: 0x4a9a54,
  dirt: 0x8b7355,
  dirtLight: 0x9b8365,
  water: 0x2980b9,
  tree: 0x1a5c2a,
  treeTrunk: 0x5c3a1e,
  // Descanso
  restArea: 0x3366aa,
} as const;

export const SIZES = {
  player: 16,
  animal: 16,
  monster: 16,
  client: 14,
  item: 8,
  coin: 6,
  station: { w: 48, h: 32 },
  tile: 32,
} as const;

// ─── Layout do Mapa v2 ───
//
// Mapa 1000x700. Área cercada central (600x350).
// Monstros spawnam nas bordas em círculo.
// Torres nos 4 cantos da cerca.
//
//  ┌─────────────────────────────────────────┐
//  │  (spawn zone - monstros)                │
//  │    ┌──[T]──────────────[T]──┐           │
//  │    │  Banco  Assador  Desc  │           │
//  │    │     Balcão   Player    │           │
//  │    │  HireZones / Upgrades  │           │
//  │    └──[T]──────────────[T]──┘           │
//  │         Clientes (entrada)              │
//  └─────────────────────────────────────────┘

export const MAP = {
  worldW: 1000,
  worldH: 700,

  // ─── Cerca (área protegida central) ───
  fence: {
    x: 200, y: 125,          // top-left
    w: 600, h: 350,           // tamanho
    centerX: 500, centerY: 300,
    // Entrada (abertura na cerca, lado sul)
    gateX: 500, gateY: 475,
    gateW: 80,
  },

  // ─── Torres (4 cantos da cerca) ───
  towers: [
    { x: 200, y: 125 },      // top-left
    { x: 800, y: 125 },      // top-right
    { x: 200, y: 475 },      // bottom-left
    { x: 800, y: 475 },      // bottom-right
  ],

  // ─── Estações DENTRO da cerca ───

  // Cozinha (assador) — centro-esquerda
  assador: {
    x: 350, y: 230,
    w: 140, h: 44,
    entradaX: 350 - 50,
    fogueiraX: 350,
    saidaX: 350 + 50,
  },

  // Balcão de venda — centro-direita
  balcaoVenda: { x: 600, y: 230 },

  // Banco — esquerda interna
  banco: { x: 260, y: 340, w: 56, h: 44 },

  // Área de descanso — direita interna (grande)
  restArea: { x: 700, y: 340, w: 80, h: 60 },

  // Spawn do jogador — centro da base
  playerSpawn: { x: 500, y: 350 },

  // ─── Hire zones (dentro da cerca, parte inferior) ───
  hireZones: {
    cacador:  { x: 280, y: 430 },
    ajudante: { x: 400, y: 430 },
    garcom:   { x: 520, y: 430 },
    contador: { x: 640, y: 430 },
    arqueiro: { x: 740, y: 430 },
  },

  // ─── Zona de clientes (fora da cerca, perto da entrada) ───
  clientZone: { x: 500, y: 530 },

  // ─── Perímetro de patrulha (ao redor da cerca, faixa de 60px) ───
  patrol: {
    x: 140, y: 65,
    w: 720, h: 470,
  },

  // ─── Monster spawn (bordas do mapa) ───
  monsterSpawn: {
    margin: 30,   // distância mínima das bordas
    // Spawn points nos 4 lados
    north: { y: 20 },
    south: { y: 680 },
    east:  { x: 970 },
    west:  { x: 30 },
  },

  // ─── Animais de caça (área entre cerca e spawn zone) ───
  huntingZone: {
    x: 50, y: 20,
    w: 900, h: 90,  // faixa norte (acima da cerca)
  },
} as const;

// ─── Vigor dos workers ───
export const VIGOR = {
  max: 100,
  drainPerSec: 0.5,
  restThreshold: 10,
  restDuration: 30,
} as const;
